'use client';

import { CategoryButton } from '@/components/ui/buttons/CategoryButton';
import { MediaShareButton } from '@/components/ui/buttons/MediaShareButton';
import { VideoButton } from '@/components/ui/buttons/VideoButton';
import { VideoBackdrop } from '@/features/video-backdrop';
import { AddToWatchListButton } from '@/features/watchlist';
import { WatchVideoButton } from '@/ui/buttons/WatchVideoButton';
import { FadeOutOverlay } from '@/ui/FadeOutOverlay';
import { Artwork } from '@/ui/images/Artwork';
import { ArtworkLogo } from '@/ui/images/ArtworkLogo';
import { CuratedAssetType } from '@vimond/services/content-discovery';
import { cn } from '@vimond/theme/tailwind';
import { getEnvVar } from '@vimond/utils/env';
import { useRouter } from 'next/navigation';

type Props = {
  asset: CuratedAssetType;
  autoplay?: boolean;
  themeBrandColor?: string | null;
  variant?: 'default' | 'alternative';
};

export const AssetHero = ({
  asset,
  autoplay,
  className,
  themeBrandColor,
  variant = 'default',
}: Props & React.ComponentPropsWithoutRef<'section'>) => {
  const router = useRouter();
  // check if asset has a video preview
  const hasVideoPreview = asset.content?.markers?.some(
    (marker) => marker.type === getEnvVar('NEXT_PUBLIC_PREVIEW_MARKER_TYPE'),
  );

  if (variant === 'default') {
    return (
      <section
        className={cn(
          'border-background-secondary box-content cursor-pointer',
          className && className,
        )}
        onClick={() => router.push(`/watch/${asset.id}`)}
      >
        <div className="h-hero relative flex w-full flex-col">
          {asset.content?.images?.defaultUrl && (
            <Artwork
              alt=""
              src={asset.content.images.defaultUrl}
              location="portal-landscape-cover"
              fallbackToDefault
              fill
              sizes="100vw"
              priority
              className="object-cover"
            />
          )}
          <FadeOutOverlay />
          {autoplay && hasVideoPreview && (
            <VideoBackdrop asset={asset} heroItem />
          )}
          <div className="animate-fade-in text-shadow absolute bottom-0 left-2 p-5 text-left text-white lg:bottom-[5%] lg:left-10">
            {asset.content?.images?.defaultUrl && (
              <ArtworkLogo src={asset.content?.images?.defaultUrl} />
            )}
            <h1 className="text-shadow text-xl font-bold lg:text-3xl">
              {asset.content?.title ?? asset.title}
            </h1>
            <div className="my-4 flex items-center gap-3">
              <WatchVideoButton asset={asset.content} />
              {!(
                getEnvVar('NEXT_PUBLIC_ALTERNATIVE_WATCH_LAYOUT') === 'true'
              ) && <VideoButton assetId={asset.id} />}
              {asset.content?.category?.id && (
                <CategoryButton categoryId={asset.content.category.id} />
              )}
              <AddToWatchListButton asset={asset} />
              {getEnvVar('NEXT_PUBLIC_ENABLE_SOCIAL_MEDIA_SHARING') !==
                'false' && (
                <MediaShareButton
                  contentId={asset.id}
                  contentTitle={asset.content?.title ?? asset.title}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    );
  } else {
    return (
      <section
        className={cn(
          'border-background-secondary box-content flex flex-col lg:flex-row gap-8 sm:px-10 lg:px-14 px-3 sm:py-10 lg:py-14 py-3',
          className && className,
        )}
        style={{
          backgroundColor: themeBrandColor ?? 'transparent',
        }}
      >
        <div className="w-full lg:w-1/2 relative flex flex-col items-center justify-center aspect-[16/9] lg:max-h-[300px]">
          {asset.content?.images?.defaultUrl && (
            <Artwork
              alt=""
              src={asset.content.images.defaultUrl}
              location="portal-landscape-cover"
              fallbackToDefault
              priority
              fill
              sizes="100vh"
              className="object-contain object-center"
            />
          )}
          {autoplay && hasVideoPreview && (
            <VideoBackdrop asset={asset} heroItem />
          )}
        </div>
        <div className="w-full lg:w-1/2 lg:p-5 flex flex-col justify-center text-white gap-2">
          <h1 className="text-2xl font-bold lg:text-3xl">
            {asset.content?.title ?? asset.title}
          </h1>
          <p className="mt-2 text-base">
            {asset.content?.['description-short'] ?? asset.content?.description}
          </p>
          <div className="mt-4 flex items-center gap-3">
            <WatchVideoButton asset={asset.content} />
            <VideoButton assetId={asset.id} />
            {asset.content?.category?.id && (
              <CategoryButton categoryId={asset.content.category.id} />
            )}
            <AddToWatchListButton asset={asset} />
            {getEnvVar('NEXT_PUBLIC_ENABLE_SOCIAL_MEDIA_SHARING') !==
              'false' && (
              <MediaShareButton
                contentId={asset.id}
                contentTitle={asset.content?.title ?? asset.title}
              />
            )}
          </div>
          {asset.content?.images?.defaultUrl && (
            <ArtworkLogo src={asset.content?.images?.defaultUrl} />
          )}
        </div>
      </section>
    );
  }
};
